import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService } from '../finance/finance-api.service';
import {
  CreateCustomerCreditLimitPayload,
  SalesApiService,
  UpdateCustomerCreditLimitPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-customer-credit-limit-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, CustomerSelectorComponent, CurrencySelectorComponent, JalaliDateInputComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.credit.limits.form.createTitle') : t('sales.credit.limits.form.editTitle')" [description]="t('sales.credit.limits.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <header class="card">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/customer-credit-limits']">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
          </div>
        </header>
        <section class="card grid">
          @if (isCreateMode()) {
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
          } @else {
            <label><span>{{ t('sales.orders.fields.customer') }}</span><input pInputText [ngModel]="customerLabel(form.customerAccountId)" readonly /></label>
          }
          <label><span>{{ t('sales.credit.fields.limit') }}</span><p-inputNumber [(ngModel)]="form.creditLimit" [min]="0" /></label>
          <label><span>{{ t('sales.credit.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
          <label><span>{{ t('sales.credit.fields.effectiveFrom') }}</span><ain-date-input [(ngModel)]="form.effectiveFromUtc" /></label>
          <label><span>{{ t('sales.credit.fields.effectiveUntil') }}</span><ain-date-input [(ngModel)]="form.effectiveUntilUtc" /></label>
          @if (!isCreateMode()) {
            <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.credit.active') }}</span></label>
          }
          <label class="full"><span>{{ t('sales.credit.fields.notes') }}</span><input pInputText [(ngModel)]="form.notes" /></label>
        </section>
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.full { grid-column: 1 / -1; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesCustomerCreditLimitDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly customers = signal<Map<string, string>>(new Map());
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');

  form = {
    customerAccountId: '' as string | null,
    creditLimit: 0,
    currencyCode: 'IRR',
    effectiveFromUtc: null as string | null,
    effectiveUntilUtc: null as string | null,
    isActive: true,
    notes: ''
  };

  ngOnInit(): void {
    this.financeApi.listPlatformCustomers({ take: 500 }).subscribe({
      next: response => {
        const map = new Map<string, string>();
        for (const item of response.items ?? []) {
          map.set(item.id, `${item.displayName} #${item.customerCode}`);
        }
        this.customers.set(map);
      },
      error: () => this.customers.set(new Map())
    });
    const limitId = this.route.snapshot.paramMap.get('limitId');
    this.isCreateMode.set(!limitId || limitId === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.loadLimit(limitId!);
  }

  customerLabel(customerId: string | null): string {
    if (!customerId) return '-';
    return this.customers().get(customerId) ?? customerId.slice(0, 8);
  }

  loadLimit(id: string): void {
    this.api.getCustomerCreditLimit(id).subscribe({
      next: limit => {
        this.form = {
          customerAccountId: limit.customerAccountId,
          creditLimit: limit.creditLimit,
          currencyCode: limit.currencyCode,
          effectiveFromUtc: limit.effectiveFromUtc ?? null,
          effectiveUntilUtc: limit.effectiveUntilUtc ?? null,
          isActive: limit.isActive,
          notes: limit.notes ?? ''
        };
        this.loading.set(false);
      },
      error: () => { this.errorMessage.set(this.t('sales.credit.limits.errorLoad')); this.loading.set(false); }
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');
    if (this.isCreateMode()) {
      if (!this.form.customerAccountId) {
        this.errorMessage.set(this.t('sales.credit.evaluate.customerRequired'));
        this.saving.set(false);
        return;
      }
      const payload: CreateCustomerCreditLimitPayload = {
        customerAccountId: this.form.customerAccountId,
        creditLimit: this.form.creditLimit,
        currencyCode: this.form.currencyCode,
        effectiveFromUtc: this.form.effectiveFromUtc,
        effectiveUntilUtc: this.form.effectiveUntilUtc,
        notes: this.form.notes || null
      };
      this.api.createCustomerCreditLimit(payload).subscribe({
        next: limit => void this.router.navigate(['/sales/customer-credit-limits', limit.id]),
        error: () => { this.errorMessage.set(this.t('sales.credit.limits.errorSave')); this.saving.set(false); }
      });
      return;
    }
    const id = this.route.snapshot.paramMap.get('limitId')!;
    const payload: UpdateCustomerCreditLimitPayload = {
      creditLimit: this.form.creditLimit,
      currencyCode: this.form.currencyCode,
      effectiveFromUtc: this.form.effectiveFromUtc,
      effectiveUntilUtc: this.form.effectiveUntilUtc,
      isActive: this.form.isActive,
      notes: this.form.notes || null
    };
    this.api.updateCustomerCreditLimit(id, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => { this.errorMessage.set(this.t('sales.credit.limits.errorSave')); this.saving.set(false); }
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
